import React, { useEffect, useRef, useState } from "react";
import { EyeOff, Trash2, Smile, MessageSquare } from "lucide-react";
import { api } from "../lib/api";
import PresenceDot from "./PresenceDot";
import ImageLightbox from "./ImageLightbox";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "🚀", "👀", "🙏", "🔥"];

// Matches http(s):// URLs and bare www.* domains. Captures the full URL.
const URL_REGEX =
    /(\bhttps?:\/\/[^\s<>"']+|\bwww\.[^\s<>"']+\.[^\s<>"']+)/gi;

function formatTime(iso) {
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
        return "";
    }
}
function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return "";
    }
}

function AttachmentView({ att, onOpen }) {
    const src = att.url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${att.url}` : att.url;
    return (
        <button
            type="button"
            onClick={() => onOpen?.(src, att.type)}
            className="block mt-2 w-fit cursor-zoom-in hover:opacity-90 transition-opacity"
            data-testid={`open-lightbox-${att.type}`}
            aria-label="Open image"
        >
            <img
                src={src}
                alt={att.type}
                className="attachment-img"
                loading="lazy"
                data-testid={`message-attachment-${att.type}`}
                draggable={false}
            />
        </button>
    );
}

/**
 * Split a text string into [text|<URL element>] parts.
 * Trailing punctuation that's likely sentence-terminating is excluded from the link.
 */
function linkifyText(text, keyPrefix = "url") {
    if (!text) return [];
    const out = [];
    let last = 0;
    URL_REGEX.lastIndex = 0;
    let m;
    while ((m = URL_REGEX.exec(text)) !== null) {
        let url = m[1];
        let tail = "";
        // Strip trailing punctuation that's likely not part of the URL.
        while (url && /[.,!?;:)\]\}>]$/.test(url)) {
            tail = url.slice(-1) + tail;
            url = url.slice(0, -1);
        }
        if (!url) continue;

        if (m.index > last) out.push(text.slice(last, m.index));
        const href = url.startsWith("http") ? url : `https://${url}`;
        out.push(
            <a
                key={`${keyPrefix}-${m.index}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-signal underline decoration-signal/40 underline-offset-2 hover:decoration-signal break-all"
                data-testid="message-link"
            >
                {url}
            </a>
        );
        if (tail) out.push(tail);
        last = m.index + m[1].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

/** Parse mentions + URLs in a single pass. Mentions take precedence. */
function renderRichContent(content, mentionIds = [], usersById = {}) {
    if (!content) return null;
    const mentionNames = mentionIds
        .map((id) => (usersById[id]?.name || "").trim())
        .filter(Boolean);

    let mentionPattern = null;
    if (mentionNames.length > 0) {
        mentionPattern = new RegExp(
            `@(${mentionNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
            "g"
        );
    }

    const parts = [];
    let last = 0;
    if (mentionPattern) {
        let m;
        while ((m = mentionPattern.exec(content)) !== null) {
            if (m.index > last) parts.push(...linkifyText(content.slice(last, m.index), `pre-${m.index}`));
            parts.push(
                <span
                    key={`mention-${m.index}`}
                    className="bg-signal/10 text-signal font-bold px-1"
                    data-testid="mention-highlight"
                >
                    @{m[1]}
                </span>
            );
            last = m.index + m[0].length;
        }
    }
    if (last < content.length) parts.push(...linkifyText(content.slice(last), `tail`));
    return parts.length ? parts : content;
}

export default function MessageList({ messages, currentUser, onMessageUpdated, usersById = {}, onOpenThread }) {
    const bottomRef = useRef(null);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    }, [messages.length]);

    const isAdmin = currentUser?.role === "admin";

    const openLightbox = (src, alt) => setLightbox({ src, alt });
    const closeLightbox = () => setLightbox(null);

    const handleHide = async (m) => {
        try {
            const res = await api.post(`/messages/${m.id}/hide`);
            onMessageUpdated?.(res.data);
        } catch (e) {
            console.error(e);
        }
    };
    const handleUnhide = async (m) => {
        try {
            const res = await api.post(`/messages/${m.id}/unhide`);
            onMessageUpdated?.(res.data);
        } catch (e) {
            console.error(e);
        }
    };
    const handleDelete = async (m) => {
        if (!window.confirm("Delete this message permanently?")) return;
        try {
            await api.delete(`/messages/${m.id}`);
            onMessageUpdated?.({ ...m, _deleted: true });
        } catch (e) {
            console.error(e);
        }
    };
    const handleReact = async (m, emoji) => {
        try {
            const res = await api.post(`/messages/${m.id}/react`, { emoji });
            onMessageUpdated?.(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    const groups = [];
    let lastDate = "";
    for (const m of messages) {
        const d = formatDate(m.created_at);
        if (d !== lastDate) {
            groups.push({ type: "divider", label: d, id: `div-${d}-${m.id}` });
            lastDate = d;
        }
        groups.push({ type: "msg", data: m });
    }

    return (
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4" data-testid="message-list">
            {messages.length === 0 && (
                <div
                    className="h-full flex flex-col items-center justify-center text-center gap-3"
                    data-testid="message-list-empty"
                >
                    <div className="ticker-label text-signal">// CHANNEL EMPTY</div>
                    <div className="font-heading font-extrabold text-3xl tracking-tight">
                        Start the conversation.
                    </div>
                    <div className="text-muted-foreground max-w-sm">
                        Send the first message, drop a picture, or search for a GIF.
                    </div>
                </div>
            )}
            {groups.map((g) =>
                g.type === "divider" ? (
                    <div key={g.id} className="flex items-center gap-3 my-4" data-testid="day-divider">
                        <div className="flex-1 h-px bg-border" />
                        <div className="ticker-label text-muted-foreground">{g.label}</div>
                        <div className="flex-1 h-px bg-border" />
                    </div>
                ) : (
                    <MessageRow
                        key={g.data.id}
                        m={g.data}
                        isAdmin={isAdmin}
                        currentUserId={currentUser?.id}
                        onHide={handleHide}
                        onUnhide={handleUnhide}
                        onDelete={handleDelete}
                        onReact={handleReact}
                        onOpenThread={onOpenThread}
                        usersById={usersById}
                        onOpenImage={openLightbox}
                    />
                )
            )}
            <div ref={bottomRef} />
            <ImageLightbox
                src={lightbox?.src}
                alt={lightbox?.alt}
                onClose={closeLightbox}
            />
        </div>
    );
}

function MessageRow({ m, isAdmin, currentUserId, onHide, onUnhide, onDelete, onReact, onOpenThread, usersById, onOpenImage }) {
    const hidden = m.hidden;
    const isOwn = m.user_id === currentUserId;
    const [pickerOpen, setPickerOpen] = useState(false);
    const initials = (m.user_name || m.user_email || "?")
        .split(" ")
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
    const mentionsMe =
        Array.isArray(m.mentions) && currentUserId && m.mentions.includes(currentUserId);
    const reactions = m.reactions || {};
    const reactionEntries = Object.entries(reactions).filter(([, arr]) => arr && arr.length);

    return (
        <div
            className={`msg-row group flex gap-3 py-2 px-2 -mx-2 ${hidden ? "opacity-60" : ""} ${
                mentionsMe ? "border-l-4 border-l-signal bg-signal/5 pl-3" : ""
            }`}
            data-testid={`message-row-${m.id}`}
        >
            <div className="relative shrink-0">
                <div className="w-9 h-9 bg-ink text-white flex items-center justify-center font-heading font-bold text-sm">
                    {initials}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5">
                    <PresenceDot userId={m.user_id} size="xs" />
                </div>
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                    <span className="font-heading font-bold text-sm" data-testid="message-author">
                        {m.user_name}
                    </span>
                    <span className="text-xs text-muted-foreground">{formatTime(m.created_at)}</span>
                    {hidden && (
                        <span className="ticker-label text-destructive" data-testid="message-hidden-label">
                            hidden by admin
                        </span>
                    )}
                    {mentionsMe && (
                        <span className="ticker-label text-signal" data-testid="mentions-me-badge">
                            mentions you
                        </span>
                    )}
                </div>
                {m.content && (
                    <div
                        className="text-base leading-relaxed whitespace-pre-wrap break-words"
                        data-testid="message-content"
                    >
                        {renderRichContent(m.content, m.mentions, usersById)}
                    </div>
                )}
                {Array.isArray(m.attachments) &&
                    m.attachments.map((a, i) => (
                        <AttachmentView key={i} att={a} onOpen={onOpenImage} />
                    ))}
                {reactionEntries.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2" data-testid="message-reactions">
                        {reactionEntries.map(([emoji, userIds]) => {
                            const mine = userIds.includes(currentUserId);
                            return (
                                <button
                                    key={emoji}
                                    onClick={() => onReact(m, emoji)}
                                    className={`flex items-center gap-1 px-2 py-0.5 border text-xs transition-colors ${
                                        mine
                                            ? "border-signal bg-signal/10 text-ink"
                                            : "border-border hover:border-ink"
                                    }`}
                                    data-testid={`reaction-${emoji}-${m.id}`}
                                >
                                    <span>{emoji}</span>
                                    <span className="font-bold">{userIds.length}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
                {m.thread_reply_count > 0 && (
                    <button
                        onClick={() => onOpenThread?.(m)}
                        className="mt-2 inline-flex items-center gap-2 px-2 py-1 border border-border hover:border-signal hover:text-signal text-xs transition-colors"
                        data-testid={`open-thread-${m.id}`}
                    >
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span className="font-bold">
                            {m.thread_reply_count} {m.thread_reply_count === 1 ? "reply" : "replies"}
                        </span>
                        <span className="text-muted-foreground">· view thread</span>
                    </button>
                )}
            </div>
            <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity relative">
                <button
                    onClick={() => setPickerOpen((v) => !v)}
                    className="p-1.5 border border-border hover:border-ink"
                    title="Add reaction"
                    data-testid={`open-reaction-picker-${m.id}`}
                >
                    <Smile className="w-3.5 h-3.5" />
                </button>
                {onOpenThread && !hidden && (
                    <button
                        onClick={() => onOpenThread(m)}
                        className="p-1.5 border border-border hover:border-ink"
                        title="Reply in thread"
                        data-testid={`reply-thread-${m.id}`}
                    >
                        <MessageSquare className="w-3.5 h-3.5" />
                    </button>
                )}
                {pickerOpen && (
                    <div
                        className="absolute right-0 top-8 z-20 bg-white border border-ink p-1 flex gap-1 shadow-lg"
                        data-testid={`reaction-picker-${m.id}`}
                    >
                        {QUICK_REACTIONS.map((e) => (
                            <button
                                key={e}
                                onClick={() => {
                                    onReact(m, e);
                                    setPickerOpen(false);
                                }}
                                className="text-lg leading-none px-1.5 py-1 hover:bg-surface"
                                data-testid={`pick-reaction-${e}-${m.id}`}
                            >
                                {e}
                            </button>
                        ))}
                    </div>
                )}
                {isAdmin && !hidden && (
                    <button
                        onClick={() => onHide(m)}
                        className="p-1.5 border border-border hover:border-ink"
                        title="Hide message"
                        data-testid={`hide-message-${m.id}`}
                    >
                        <EyeOff className="w-3.5 h-3.5" />
                    </button>
                )}
                {isAdmin && hidden && (
                    <button
                        onClick={() => onUnhide(m)}
                        className="p-1.5 border border-border hover:border-ink text-xs"
                        data-testid={`unhide-message-${m.id}`}
                    >
                        Unhide
                    </button>
                )}
                {(isAdmin || isOwn) && (
                    <button
                        onClick={() => onDelete(m)}
                        className="p-1.5 border border-border hover:border-destructive hover:text-destructive"
                        title="Delete message"
                        data-testid={`delete-message-${m.id}`}
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}
